package org.teddapp.project.ui.habits

import androidx.compose.foundation.layout.widthIn

import org.teddapp.project.ui.TeddEmptyState

import org.teddapp.project.ui.TeddCard

import org.teddapp.project.ui.TeddButtonKind

import org.teddapp.project.ui.TeddButton

import org.teddapp.project.ui.TeddSpace

import org.teddapp.project.ui.TeddScreenHeader

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.InkButton
import org.teddapp.project.ui.TeddInkBlock
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.adaptive.WindowClass
import org.teddapp.project.ui.adaptive.WithWindowClass
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.nav.TeddBottomNav
import org.teddapp.project.ui.nav.TeddNavRail
import org.teddapp.project.ui.settings.SettingsDrawerHost
import org.teddapp.project.ui.settings.rememberSettingsDrawer

/**
 * Habitudes — observer l'envie plutôt que la combattre (Brewer). Squelette de la phase 2 :
 * le pilier existe dans la navigation, le parcours « J'ai une envie » arrive en phase 7.
 */
@Composable
fun HabitsScreen(
    repos: TeddRepositories,
    onNavigate: (Section) -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val model = rememberHabitsModel(repos)
    WithWindowClass(modifier = modifier) { wc ->
        when (wc) {
            WindowClass.Compact -> HabitsCompactLayout(model, onNavigate)
            else -> HabitsExpandedLayout(model, onNavigate)
        }
    }
    when (model.step) {
        CravingStep.Naming -> CravingNamingScreen(model)
        CravingStep.Describing -> CravingDescribingScreen(model)
        CravingStep.Guidance -> CravingGuidanceScreen(model)
        CravingStep.Idle -> Unit
    }
}

@Composable
private fun HabitsExpandedLayout(model: HabitsModel, onNavigate: (Section) -> Unit) {
    val drawer = rememberSettingsDrawer()
    SettingsDrawerHost(drawer, onNavigate = onNavigate) {
        Row(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
            TeddNavRail(active = Section.Habits, onNavigate = onNavigate, onSettingsClick = drawer::open)
            Column(
                modifier = Modifier.weight(1f).fillMaxHeight().verticalScroll(rememberScrollState())
                    .padding(start = TeddSpace.S8, end = TeddSpace.S8, top = TeddSpace.S8, bottom = TeddSpace.S12),
                verticalArrangement = Arrangement.spacedBy(TeddSpace.S6),
            ) {
                TeddScreenHeader(
                    title = "Habitudes",
                    overline = when (val n = model.totalObservations) {
                        0 -> "Aucune observation"
                        1 -> "1 observation · ${model.totalCrossings} traversée"
                        else -> "$n observations · ${model.totalCrossings} traversées"
                    },
                ) {
                    TeddButton("J'ai une envie", model::startCraving, kind = TeddButtonKind.Primary, icon = "habits")
                }
                // Le contenu est borné : un écran large ne doit pas étirer des cartes de chiffres.
                Column(Modifier.widthIn(max = 880.dp), verticalArrangement = Arrangement.spacedBy(TeddSpace.S5)) {
                    HabitsBody(model, showEntry = false)
                }
            }
        }
    }
}

@Composable
private fun HabitsCompactLayout(model: HabitsModel, onNavigate: (Section) -> Unit) {
    Box(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
        Column(modifier = Modifier.fillMaxSize()) {
            Column(
                modifier = Modifier.weight(1f).fillMaxWidth().statusBarsPadding().verticalScroll(rememberScrollState()).padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(14.dp),
            ) {
                Text("Habitudes", style = TeddType.Title1, color = TeddTokens.Ink)
                HabitsBody(model)
            }
            TeddBottomNav(active = Section.Habits, onNavigate = onNavigate, modifier = Modifier.navigationBarsPadding())
        }
    }
}

@Composable
private fun HabitsBody(model: HabitsModel, showEntry: Boolean = true) {
    // Sur téléphone, le bloc d'encre est le point d'entrée. Sur grand écran, le bouton
    // de l'en-tête joue ce rôle : le bloc ferait doublon.
    if (showEntry) {
        TeddInkBlock(
            eyebrow = "Un seul point d'entrée",
            title = "J'ai une envie",
            subtitle = "Observer l'envie plutôt que la combattre : sensations, intensité, issue. Céder compte autant que traverser.",
        ) {
            Row(modifier = Modifier.padding(top = 4.dp)) {
                InkButton(text = "Commencer l'observation", onClick = model::startCraving)
            }
        }
    }
    if (model.totalObservations == 0) {
        TeddCard(modifier = Modifier.fillMaxWidth()) {
            TeddEmptyState(
                title = "Aucune observation pour l'instant",
                message = "Il n'y a pas de chaîne de jours à ne pas casser : chaque envie observée compte, " +
                    "qu'elle soit traversée ou non.",
                icon = "habits",
                actionLabel = "Observer une envie",
                onAction = model::startCraving,
                modifier = Modifier.fillMaxWidth(),
            )
        }
    } else {
        HabitTallies(
            tallies = model.tallies,
            validatedToday = { model.validatedToday(it) },
            onValidate = { model.validate(it) },
        )
    }
}
